import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, throwError, BehaviorSubject, Subscription } from 'rxjs';
import { catchError, retry, map } from 'rxjs/operators';
import { Estimativa } from 'src/app/models/Estimativa';
import { environment } from 'src/environments/environment';
import { Estimate } from 'src/app/models/Estimate';
import { httpOptions } from "./headers";
import { EstimationState } from 'src/app/models/EstimationState';

@Injectable({
  providedIn: 'root'
})

// Class responsible for communicating with the estimativa backend.
export class ApiEstimativaService {

  private estimativaUrl = environment.estimativaUrl;
  private calculateUrl = environment.calculateUrl;
  private calculateAssociationUrl = environment.calculateAssociationUrl;
  private excelUrl = environment.excelUrl;
  private weeklyExcelUrl = environment.weeklyExcelUrl;
  private excelToJiraUrl = environment.excelToJiraUrl;
  private manualUrl = environment.manualUrl;

  private httpOptions(session: string) {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + session
      }),
    };
  };

  constructor(private http: HttpClient) { }

  saveEstimate(estimate: any): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post<Estimate>(this.estimativaUrl + "/add", estimate, this.httpOptions(token));
  }

  getEstimateById(id: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get<Estimate>(this.estimativaUrl + '/' + encodeURIComponent(id), this.httpOptions(token));
  }

  updateEstimate(estimateId: string, estimate: any): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<Estimate>(this.estimativaUrl + '/' + estimateId + '/update', estimate, this.httpOptions(token));
  }

  deleteEstimate(estimateId: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.delete<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/delete', this.httpOptions(token));
  }

  updateEstimateTimes(estimate: Estimate): Observable<Estimate> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<Estimate>(this.estimativaUrl + '/' + estimate.id + '/updateTimes', estimate, this.httpOptions(token));
  }

  calculateEst(estimate: any): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post<Estimate>(this.calculateUrl, estimate, this.httpOptions(token));
  }

  calculate(estimateId: String): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get<Estimate>(this.calculateUrl + "/" + estimateId , this.httpOptions(token));
  }

  certifyEstimate(estimateId: string, comment: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/certify', {
      "architectComment": comment || ""
    }, this.httpOptions(token));
  }

  closeEstimate(estimateId: string, comment: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/close', {
      "closingComment": comment || ""
    }, this.httpOptions(token));
  }

  approveEstimate(estimateId: string, comment: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/approve', {
      "approvalComment": comment || ""
    }, this.httpOptions(token));
  }

  rejectEstimate(estimateId: string, comment: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/reject', {
      "rejectionComment": comment || ""
    }, this.httpOptions(token));
  }

  toFixEstimate(estimateId: string, comment: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/to-fix', {
      "fixComment": comment || ""
    }, this.httpOptions(token));
  }


  addReview(estimateId: string, comment: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/add-review', {
      "reviewComment": comment || ""
    }, this.httpOptions(token));
  }

  getProjectEstimates(projectId: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get<any>(this.estimativaUrl + '/project/' + encodeURIComponent(projectId), this.httpOptions(token));
  }

  associateEstimate(estimateIds: string[], folderName: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/associate', {
      "estimateIds": estimateIds,
      "associatedName": folderName
    }, this.httpOptions(token))
  }

  getAssociationResult(estimateIds: string[]) {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post<Estimate>(this.calculateAssociationUrl, {
      "estimateIds": estimateIds
    }, this.httpOptions(token));
  }

  removerAssociationEstimate(estimateId: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.put<any>(this.estimativaUrl + '/' + encodeURIComponent(estimateId) + '/remove-association', {}, this.httpOptions(token))
  }

  exportExcel(estimateId: any) {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get(this.excelUrl + '/' + estimateId, { headers: this.httpOptions(token).headers, responseType: 'blob' })
  }

  exportAssociationExcel(estimateIds: string[]) {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post(this.excelUrl + "/associationResult", {
      "estimateIds": estimateIds
    }, { headers: this.httpOptions(token).headers, responseType: 'blob' })
  }

  myEstimates(): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get<any>(this.estimativaUrl + "/my", this.httpOptions(token));
  }

  downloadManual(language: string) {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get(this.manualUrl + language, { headers: this.httpOptions(token).headers, responseType: 'blob' })
  }


  updateCriticalityResult(estimate: Estimate): Observable<Estimate> {
    return this.http.put<Estimate>(this.estimativaUrl + '/updateCritResult/' + estimate.id, estimate, httpOptions);
  }

  fetchData() {
    return this.http.get(this.estimativaUrl, httpOptions).pipe(map((data: any) => {
      return data;
    }))
  }

  exportToJira(estimate: any) {
    console.log(estimate);

    return this.http.post(this.excelToJiraUrl + '/subtasks', estimate, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + 'NTQ1MjMzMjk4NjU0OkKGLvut4Y5W1hJm4eLIUkLn7PDt'
      }),
      responseType: 'text'
    })
  }


  exportDataFromJira() {

    return this.http.get(this.excelToJiraUrl + '/epics?projectKey=TESPT', {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + 'NTQ1MjMzMjk4NjU0OkKGLvut4Y5W1hJm4eLIUkLn7PDt'
      }),
      responseType: 'json'
    })
  }


  exportWeeklyExcel() {
    return this.http.get(this.weeklyExcelUrl, { headers: httpOptions.headers, responseType: 'blob' })
  }

  searchEstimates(search: string, states: EstimationState[], time: string, order: string, page: number, size: number) {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    let uri: string = this.estimativaUrl + '/search?';
    if (search) {
      uri += 'search=' + encodeURIComponent(search) + '&';
    }
    if (states && states.length > 0) {
      for (const state of states) {
        uri += 'state=' + encodeURIComponent(state) + '&';
      }
    }
    if (time) {
      uri += 'time=' + encodeURIComponent(time) + '&';
    }
    if (order) {
      uri += 'order=' + encodeURIComponent(order) + '&';
    }
    if (page) {
      uri += 'page=' + page + '&';
    }
    if (size) {
      uri += 'size=' + size + '&';
    }
    return this.http.get<any>(uri, this.httpOptions(token));
  }

  /*

    Passing data with a service

  */

  private apiData = new BehaviorSubject<any>(null);
  public apiData$ = this.apiData.asObservable();

  // here we set/change value of the observable
  setData(data: any) {

    this.apiData.next(data)
  }

  // ---- Actual Imputation ----

  getAllImputations(): Observable<any[]> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get<any[]>(this.estimativaUrl + '/imputation/all', this.httpOptions(token));
  }

  getImputation(estimateId: string): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.get<any>(this.estimativaUrl + '/imputation/' + encodeURIComponent(estimateId), this.httpOptions(token));
  }

  saveImputation(request: any): Observable<any> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post<any>(this.estimativaUrl + '/imputation/save', request, this.httpOptions(token));
  }

  exportFullReport(reportData: any) {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post(this.excelUrl + "/fullReport", reportData, { headers: this.httpOptions(token).headers, responseType: 'blob' });
  }

  uploadFunctionalFile(file: File): Observable<{ filePath: string }> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<{ filePath: string }>(
      this.estimativaUrl + '/ai/upload-functional-file',
      formData,
      { headers: new HttpHeaders({ 'Authorization': 'Bearer ' + token }) }
    );
  }

  generateActivities(payload: {
    catalogId: string;
    estimateId?: string;
    estimateName: string;
    methodology: string;
    estimationModel?: string;
    filePath: string;
  }): Observable<{ macroActivities: any[]; actionItems: any[] }> {
    const token = JSON.parse(sessionStorage.getItem('authToken') as string).token;
    return this.http.post<{ macroActivities: any[]; actionItems: any[] }>(
      this.estimativaUrl + '/ai/generate-activities',
      payload,
      this.httpOptions(token)
    );
  }
}
